import { Alert, Row, Spin } from 'antd'
import {
  DrilldownDataContext,
  useBufferedFilteredEntries,
} from 'features/drilldown'
import {
  useFocusItems,
  useGetPrecedingFilters,
} from 'features/drilldown/data/DrilldownFilterContext'
import {
  useFilteredIds,
  useGetLinkedIds,
  useHistogramQueries,
} from 'features/drilldown/rolodex/dataHelpers/drilldownDataHelpers'
import { useDrilldownSchema } from 'features/drilldown/rolodex/DrilldownDataSchema'
import { DrilldownRolodexType } from 'features/drilldown/rolodex/ontology/ontologyTypes'
import { useRolodexHistogram } from 'features/rolodex'
import { MapFeaturePattern } from 'features/siteMapViz'
import { DefaultSiteViewContextProvider } from 'features/siteView'
import _ from 'lodash'
import {
  MapInteractionAPIs,
  SiteViewMap,
  // Disabled temporarily until we have a first-class Site View component
  // eslint-disable-next-line boundaries/element-types
} from 'pages/siteView/siteMap/SiteViewMap'
import React, {
  Dispatch,
  SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'
import styled from 'styled-components'
import CantileverTheme from 'styles/cantileverTheme'
import { GeckoSite, GenericPanelData, RolodexEntryType } from 'types'

const LATEST_DATA_SCHEMA_VERSION = 1

export const DEFAULT_SITE_PANEL_DATA: GenericPanelData = {
  panelDataSchemaVersion: LATEST_DATA_SCHEMA_VERSION,
  title: 'Site View',
  isPinned: true,
}

// TODO: These hardcoded pixel values will get messed up if the header sizes change
const AppContainer = styled.div<{ $isPresentationModeEnabled: boolean }>`
  display: flex;
  flex-direction: row;
  height: calc(
    100vh - ${(props) => (props.$isPresentationModeEnabled ? '140px' : '210px')}
  );
  overflow: hidden;
`

const LoadingOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(255, 255, 255, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
`

// TODO(xdowns): At some point, we want to update this to:
//  -take only the latest inspection per unit
//  - Link from those to CDSs
// This will handle assets where we do multiple inspections
function useGetAssetsWithLinked(linkedType: DrilldownRolodexType): {
  isLoading: boolean
  data: ReadonlySet<string>
} {
  const {
    queries: [histoQuery],
    isLoading: queryLoading,
  } = useHistogramQueries({
    select: useMemo(
      () => [
        {
          field: 'id',
          type: 'unit',
          group_by: true,
          label: 'unit_id',
        },
        {
          field: 'id',
          type: linkedType,
          op: 'count',
          label: 'count',
        },
      ],
      [linkedType]
    ),
    filters: useGetPrecedingFilters(),
  })

  const { data, isLoading } = useRolodexHistogram(histoQuery)
  const result = useMemo(
    () =>
      _.flatten(data).map((rec) => ({
        unitId: rec['unit_id'] as string,
        count: rec['count'] as number,
      })),
    [data]
  )
  return useMemo(
    () => ({
      data: new Set(
        result.filter((rec) => rec.count > 0).map((rec) => rec.unitId)
      ),
      isLoading: queryLoading || isLoading,
    }),
    [isLoading, queryLoading, result]
  )
}

type InspectionStatus = 'not_inspected' | 'inspected' | 'scheduled'
const INSPECTION_STATUS_COLORS: Record<InspectionStatus, string> = {
  inspected: CantileverTheme.colors.SeaGreen.trim(),
  scheduled: '#BDC1F0',
  not_inspected: CantileverTheme.colors.grayscale[400].trim(),
}
const INSPECTION_LEGEND_LABELS: Record<InspectionStatus, string> = {
  inspected: 'Inspected',
  not_inspected: 'Not Inspected',
  scheduled: 'Scheduled',
}

const InspectionLegend: React.FC = () => {
  return (
    <div
      style={{
        position: 'absolute',
        bottom: '16px',
        left: '16px',
        backgroundColor: 'rgba(10, 10, 10, 0.8)',
        borderRadius: '8px',
        padding: '12px',
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
        zIndex: 1000,
      }}>
      {Object.entries(INSPECTION_STATUS_COLORS).map(([key, color]) => (
        <div
          key={key}
          style={{
            display: 'flex',
            alignItems: 'center',
            marginBottom: '8px',
          }}>
          <div
            style={{
              width: '16px',
              height: '16px',
              backgroundColor: color,
              marginRight: '8px',
              borderRadius: '2px',
              flexShrink: 0,
            }}
          />
          <span
            style={{
              fontSize: '14px',
              color: CantileverTheme.colors.gray[300],
            }}>
            {INSPECTION_LEGEND_LABELS[key as InspectionStatus]}
          </span>
        </div>
      ))}
    </div>
  )
}

function useGetFlyToId(activeAssetIds: ReadonlyArray<string>): {
  flyToId: string | undefined
  isLoading: boolean
} {
  const [focusItems] = useFocusItems()
  const { entryTypeNames, isDescendant, findLowestType } = useDrilldownSchema()
  const entryTypes = useMemo(
    () => Object.keys(entryTypeNames),
    [entryTypeNames]
  )
  // Pick the lowest entry type which:
  //   - has a focus value
  //   - is a descendant of 'unit'
  const focusType: DrilldownRolodexType | undefined = useMemo(
    () =>
      _.first(
        entryTypes
          .filter((type) => isDescendant(type, 'unit'))
          .toSorted((t1, t2) => {
            if (t1 === t2) return 0
            if (findLowestType([t1, t2]) === t1) {
              return -1
            }
            return 1
          })
          .filter((type) => !!focusItems?.get(type as RolodexEntryType)?.length)
      ),
    [entryTypes, findLowestType, focusItems, isDescendant]
  )
  const { links: linkedToUnit, isLoading } = useGetLinkedIds(
    focusItems?.get((focusType as RolodexEntryType) ?? RolodexEntryType.UNIT) ??
      [],
    'unit'
  )
  const findActiveFlyToId = useCallback(
    (ids: ReadonlyArray<string> | undefined) => {
      const potentials = ids?.filter((ft) => activeAssetIds.includes(ft))
      if (potentials?.length === 1) {
        return potentials[0]
      }
      return undefined
    },
    [activeAssetIds]
  )
  const flyToId: string | undefined = useMemo(() => {
    const inspectionLink = findActiveFlyToId([...linkedToUnit.values()])
    return inspectionLink
  }, [findActiveFlyToId, linkedToUnit])

  return useMemo(
    () => ({
      isLoading,
      flyToId,
    }),
    [flyToId, isLoading]
  )
}

/**
 * Figure out if we should be flying to a unit.
 * This will get any focus-items, and try to fly to them.
 * So far, in order of preference:
 *  - if you have a focused unit id that's in the active units, fly to it
 *  - Look at any focused inspections, and see if their unit is in the active units
 *  ... TODO expet to add more here, like indications etc
 */
function useFlyTo(activeAssetIds: ReadonlyArray<string>): {
  unitIdFlying: [
    string | undefined,
    Dispatch<SetStateAction<string | undefined>>,
  ]
  isLoading: boolean
} {
  const [unitIDFlyingTo, setUnitIDFlyingTo] = useState<string | undefined>(
    undefined
  )
  const { flyToId, isLoading: flyToLoading } = useGetFlyToId(activeAssetIds)
  useEffect(() => {
    if (flyToId) {
      setUnitIDFlyingTo(flyToId)
    }
  }, [flyToId, setUnitIDFlyingTo])

  useEffect(() => {}, [])
  return useMemo(
    () => ({
      unitIdFlying: [unitIDFlyingTo, setUnitIDFlyingTo],
      isLoading: flyToLoading,
    }),
    [flyToLoading, unitIDFlyingTo]
  )
}

export const RawSiteView: React.FC = () => {
  const { data: sites, isLoading: isLoadingSites } = useBufferedFilteredEntries(
    {
      entryType: 'site',
      limit: 2,
      dataKey: 'sites',
    }
  )

  const { ids: activeAssetIds, isLoading: unitsLoading } =
    useFilteredIds('unit')

  const { data: inspectedUnitIds, isLoading: inspectionsLoading } =
    useGetAssetsWithLinked('component-dataset')
  const { data: scheduledUnitIds, isLoading: scheduledLoading } =
    useGetAssetsWithLinked('inspection')

  const { isPresentationModeEnabled } = useContext(DrilldownDataContext)

  const getUnitDisplayProperties = useCallback(
    (unitId: string | null) => ({
      color:
        unitId && activeAssetIds.includes(unitId)
          ? inspectedUnitIds.has(unitId)
            ? INSPECTION_STATUS_COLORS['inspected']
            : scheduledUnitIds.has(unitId)
              ? INSPECTION_STATUS_COLORS['scheduled']
              : INSPECTION_STATUS_COLORS['not_inspected']
          : CantileverTheme.colors.grayscale[400],
      pattern: MapFeaturePattern.Flat,
    }),
    [activeAssetIds, inspectedUnitIds, scheduledUnitIds]
  )

  const {
    unitIdFlying: [unitIDFlyingTo, setUnitIDFlyingTo],
    isLoading: flyToLoading,
  } = useFlyTo(activeAssetIds)

  const mapInteractionAPIs = useMemo<MapInteractionAPIs>(
    () => ({
      unitIDFlyingTo,
      setUnitIDFlyingTo,
    }),
    [setUnitIDFlyingTo, unitIDFlyingTo]
  )

  if (isLoadingSites && sites.length === 0) {
    return (
      <Row justify='center' align='middle' style={{ height: '50px' }}>
        <Spin />
      </Row>
    )
  }

  if (sites.length !== 1) {
    return (
      <Alert
        message='There needs to be exactly one site in scope to see site view'
        type='error'
        showIcon
      />
    )
  }

  const site: GeckoSite = sites[0]

  return (
    <AppContainer $isPresentationModeEnabled={isPresentationModeEnabled}>
      <DefaultSiteViewContextProvider currentSite={site}>
        <SiteViewMap
          site={site}
          getUnitDisplayProperties={getUnitDisplayProperties}
          componentToDisplayOnFeatureClick={() => null}
          mapInteractionAPIs={mapInteractionAPIs}
        />
        <InspectionLegend />
        {(isLoadingSites ||
          unitsLoading ||
          inspectionsLoading ||
          scheduledLoading ||
          flyToLoading) && (
          <LoadingOverlay>
            <Spin />
          </LoadingOverlay>
        )}
      </DefaultSiteViewContextProvider>
    </AppContainer>
  )
}

export const SiteView = React.memo(RawSiteView)
